// The rental cart: what a visitor is putting together before requesting a booking.
// Kept in localStorage so it survives navigation and the Facebook sign-in round trip,
// and shared by /book, the product pages and the navbar badge.
import * as React from 'react';

export type CartItem = { assetTag: string; name: string; dailyRate: string | null; pieces: number };
export type Cart = { startOn: string; endOn: string; items: CartItem[] };

export const CART_KEY = 'lwm.book.cart';
const CHANGE_EVENT = 'lwm:cart';

export const isoDate = (d: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const defaultCart = (): Cart => {
    const start = new Date();
    start.setDate(start.getDate() + 1);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    return {startOn: isoDate(start), endOn: isoDate(end), items: []};
};

export const loadCart = (): Cart => {
    try {
        const raw = localStorage.getItem(CART_KEY);
        if (raw) {
            const cart = JSON.parse(raw) as Cart;
            if (cart.startOn >= isoDate(new Date())) return cart;
        }
    } catch {
        // Fall through to a fresh cart.
    }
    return defaultCart();
};

export const saveCart = (cart: Cart) => {
    try {
        localStorage.setItem(CART_KEY, JSON.stringify(cart));
    } catch {
        // Storage may be unavailable (private mode); the in-memory copy still works.
    }
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
};

export const clearCart = () => {
    try { localStorage.removeItem(CART_KEY); } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
};

export const addToCart = (item: CartItem): Cart => {
    const cart = loadCart();
    if (!cart.items.some((i) => i.assetTag === item.assetTag)) cart.items.push(item);
    saveCart(cart);
    return cart;
};

export const removeFromCart = (assetTag: string): Cart => {
    const cart = loadCart();
    cart.items = cart.items.filter((i) => i.assetTag !== assetTag);
    saveCart(cart);
    return cart;
};

export const inCart = (assetTag: string) => loadCart().items.some((i) => i.assetTag === assetTag);

// Re-renders on any cart change in this tab (custom event) or another tab (storage event).
export const useCart = (): Cart => {
    const [cart, setCart] = React.useState<Cart>(loadCart);
    React.useEffect(() => {
        const refresh = () => setCart(loadCart());
        window.addEventListener(CHANGE_EVENT, refresh);
        window.addEventListener('storage', refresh);
        return () => {
            window.removeEventListener(CHANGE_EVENT, refresh);
            window.removeEventListener('storage', refresh);
        };
    }, []);
    return cart;
};
